import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to get full media URL
export const getMediaUrl = (relativeUrl) => {
  if (!relativeUrl) return '';
  // If it's already a full URL, return as is
  if (relativeUrl.startsWith('http')) return relativeUrl;
  // If it starts with /api/media or /media, prepend backend URL
  if (relativeUrl.startsWith('/api/media') || relativeUrl.startsWith('/media')) {
    return `${BACKEND_URL}${relativeUrl}`;
  }
  // Otherwise return as is
  return relativeUrl;
};

// Create axios instance
const axiosInstance = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle response errors
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('userId');
      window.dispatchEvent(new Event('auth-logout'));
      // Mark as auth error so components skip toast
      error._isAuthError = true;
    }
    return Promise.reject(error);
  }
);

// Auth APIs
export const authAPI = {
  register: (data) => axiosInstance.post('/auth/register', data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  verifyCode: (email, code) => {
    const data = new FormData();
    data.append('email', email);
    data.append('code', code);
    return axiosInstance.post('/auth/verify-code', data, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  completeProfile: (data) => axiosInstance.post('/auth/complete-profile', data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  resendCode: (email) => {
    const data = new FormData();
    data.append('email', email);
    return axiosInstance.post('/auth/resend-code', data, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  login: (data) => axiosInstance.post('/auth/login', data),
  getMe: () => axiosInstance.get('/auth/me'),
  logout: () => axiosInstance.post('/auth/logout'),
  verifyEmail: (token) => axiosInstance.get('/auth/verify-email', { params: { token } }),
  forgotPassword: (email) => axiosInstance.post('/auth/forgot-password', null, { params: { email } }),
  resetPassword: (token, newPassword) => axiosInstance.post('/auth/reset-password', null, { params: { token, new_password: newPassword } }),
  resendVerification: (email) => axiosInstance.post('/auth/resend-verification', null, { params: { email } }),
  changePassword: (currentPassword, newPassword) => axiosInstance.post('/auth/change-password', null, { params: { current_password: currentPassword, new_password: newPassword } }),
  checkEmail: (email) => axiosInstance.get('/auth/check-email', { params: { email } }),
  setParentEmail: (email, parentEmail) => {
    const data = new FormData();
    data.append('email', email);
    data.append('parentEmail', parentEmail);
    return axiosInstance.post('/auth/set-parent-email', data, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  parentalConsent: (token) => axiosInstance.get('/auth/parental-consent', { params: { token } }),
  resendParentalConsent: (email) => {
    const data = new FormData();
    data.append('email', email);
    return axiosInstance.post('/auth/resend-parental-consent', data, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// User APIs
export const userAPI = {
  getMyProfile: () => axiosInstance.get('/users/me'),
  getUserProfile: (username) => axiosInstance.get(`/users/${username}`),
  updateProfile: (data) => axiosInstance.put('/users/profile', data),
  updateStats: (stats) => axiosInstance.put('/users/stats', { stats }),
  uploadProfileImage: (formData) => axiosInstance.post('/users/upload-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  followUser: (userId, message = '') => {
    const data = new FormData();
    if (message) data.append('message', message);
    return axiosInstance.post(`/users/${userId}/follow`, data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  unfollowUser: (userId) => axiosInstance.delete(`/users/${userId}/follow`),
  getFollowStatus: (userId) => axiosInstance.get(`/users/${userId}/follow-status`),
  getFollowers: (userId, skip = 0, limit = 20) => axiosInstance.get(`/users/${userId}/followers`, { params: { skip, limit } }),
  getFollowing: (userId, skip = 0, limit = 20) => axiosInstance.get(`/users/${userId}/following`, { params: { skip, limit } }),
  updatePrivacy: (isPrivate) => {
    const data = new FormData();
    data.append('isPrivate', isPrivate);
    return axiosInstance.put('/users/privacy', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getReceivedFollowRequests: () => axiosInstance.get('/users/follow-requests/received'),
  getSentFollowRequests: () => axiosInstance.get('/users/follow-requests/sent'),
  acceptFollowRequest: (requestId) => axiosInstance.post(`/users/follow-requests/${requestId}/accept`),
  rejectFollowRequest: (requestId) => axiosInstance.post(`/users/follow-requests/${requestId}/reject`),
  cancelFollowRequest: (userId) => axiosInstance.delete(`/users/follow-requests/${userId}/cancel`),
  searchUsers: (query, skip = 0, limit = 20) => axiosInstance.get('/users/search', { params: { q: query, skip, limit } }),
  getSuggestedUsers: (limit = 10) => axiosInstance.get('/users/suggested', { params: { limit } }),
};

// Post APIs
export const postAPI = {
  uploadMedia: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return axiosInstance.post('/posts/upload-media', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  createPost: (content, mediaUrls = [], tags = []) => {
    const formData = new FormData();
    formData.append('content', content || '');
    formData.append('mediaUrls', JSON.stringify(mediaUrls));
    formData.append('tags', JSON.stringify(tags));
    return axiosInstance.post('/posts', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getFeed: (page = 1, limit = 10) => axiosInstance.get('/posts/feed', { params: { page, limit } }),
  getExplore: (page = 1, limit = 30) => axiosInstance.get('/posts/explore', { params: { page, limit } }),
  getPost: (postId) => axiosInstance.get(`/posts/${postId}`),
  getStories: () => axiosInstance.get('/posts/stories/all'),
  getUserPosts: (username, page = 1, limit = 10) => axiosInstance.get(`/posts/user/${username}`, { params: { page, limit } }),
  deletePost: (postId) => axiosInstance.delete(`/posts/${postId}`),
  likePost: (postId) => axiosInstance.post(`/posts/${postId}/like`),
  unlikePost: (postId) => axiosInstance.delete(`/posts/${postId}/like`),
  addComment: (postId, text) => {
    const formData = new FormData();
    formData.append('text', text);
    return axiosInstance.post(`/posts/${postId}/comment`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteComment: (postId, commentId) => axiosInstance.delete(`/posts/${postId}/comment/${commentId}`),
  likeComment: (postId, commentId) => axiosInstance.post(`/posts/${postId}/comment/${commentId}/like`),
  unlikeComment: (postId, commentId) => axiosInstance.delete(`/posts/${postId}/comment/${commentId}/like`),
};

// Trial APIs
export const trialAPI = {
  createTrial: (data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      // Skip null/undefined values and handle image file specially
      if (data[key] !== null && data[key] !== undefined && data[key] !== '') {
        if (key === 'image' && data[key] instanceof File) {
          formData.append('image', data[key]);
        } else if (key !== 'image') {
          formData.append(key, data[key]);
        }
      }
    });
    return axiosInstance.post('/trials', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getTrials: (sport = null, location = null, status = 'open', page = 1, limit = 12) => 
    axiosInstance.get('/trials', { params: { sport, location, status, page, limit } }),
  getTrial: (trialId) => axiosInstance.get(`/trials/${trialId}`),
  updateTrial: (trialId, data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== undefined) {
        formData.append(key, data[key]);
      }
    });
    return axiosInstance.put(`/trials/${trialId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteTrial: (trialId) => axiosInstance.delete(`/trials/${trialId}`),
  applyToTrial: (trialId, message, cvFile = null, videoFile = null) => {
    const formData = new FormData();
    formData.append('message', message);
    if (cvFile) formData.append('cv_file', cvFile);
    if (videoFile) formData.append('video_file', videoFile);
    return axiosInstance.post(`/trials/${trialId}/apply`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getTrialApplications: (trialId) => axiosInstance.get(`/trials/${trialId}/applications`),
  updateApplicationStatus: (applicationId, status) => {
    const formData = new FormData();
    formData.append('status', status);
    return axiosInstance.put(`/trials/applications/${applicationId}/status`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getMyApplications: () => axiosInstance.get('/trials/my-applications'),
  trackTrialView: (trialId) => axiosInstance.post(`/trials/${trialId}/view`),
};


// AI Chat APIs
export const aiAPI = {
  chat: (message, sessionId = null) => 
    axiosInstance.post('/ai/chat', { message, sessionId }),
  getChatHistory: (sessionId) => axiosInstance.get(`/ai/chat/history/${sessionId}`),
  getUserSessions: () => axiosInstance.get('/ai/chat/sessions'),
  deleteSession: (sessionId) => axiosInstance.delete(`/ai/chat/session/${sessionId}`),
};

// Chat/Messaging APIs
export const chatAPI = {
  getConversations: () => axiosInstance.get('/chat/conversations'),
  getOrCreateConversation: (otherUserId) => axiosInstance.get(`/chat/conversation/${otherUserId}`),
  getMessages: (conversationId, skip = 0, limit = 50) => 
    axiosInstance.get(`/chat/messages/${conversationId}`, { params: { skip, limit } }),
  sendMessage: (receiverId, content) => 
    axiosInstance.post('/chat/send', { receiverId, content }),
  sendMessageWithAttachment: (receiverId, content, file) => {
    const formData = new FormData();
    formData.append('receiverId', receiverId);
    formData.append('content', content || '');
    formData.append('file', file);
    return axiosInstance.post('/chat/send-with-attachment', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  markRead: (conversationId) => axiosInstance.post(`/chat/mark-read/${conversationId}`),
  deleteConversation: (conversationId) => axiosInstance.delete(`/chat/conversation/${conversationId}`),
};

// Jobs APIs
export const jobsAPI = {
  getJobs: (params = {}) => axiosInstance.get('/jobs', { params }),
  getJob: (jobId) => axiosInstance.get(`/jobs/${jobId}`),
  createJob: (data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== undefined) {
        formData.append(key, data[key]);
      }
    });
    return axiosInstance.post('/jobs', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  updateJob: (jobId, data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== undefined) {
        formData.append(key, data[key]);
      }
    });
    return axiosInstance.put(`/jobs/${jobId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteJob: (jobId) => axiosInstance.delete(`/jobs/${jobId}`),
  getMyJobs: (status = null) => axiosInstance.get('/jobs/my-jobs', { params: status ? { status } : {} }),
  applyToJob: (jobId, coverLetter, cvFile = null) => {
    const formData = new FormData();
    formData.append('coverLetter', coverLetter);
    if (cvFile) formData.append('cv_file', cvFile);
    return axiosInstance.post(`/jobs/${jobId}/apply`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getMyApplications: () => axiosInstance.get('/jobs/my-applications'),
  getReceivedJobApplications: (status = null) => axiosInstance.get('/jobs/received-applications', { params: status ? { status } : {} }),
  updateJobApplicationStatus: (applicationId, status) => axiosInstance.put(`/jobs/applications/${applicationId}/status?status=${status}`),
};

// Discovery APIs
export const discoveryAPI = {
  discoverAthletes: (params = {}) => axiosInstance.get('/discovery/athletes', { params }),
  discoverProfessionals: (params = {}) => axiosInstance.get('/discovery/professionals', { params }),
  discoverClubs: (params = {}) => axiosInstance.get('/discovery/clubs', { params }),
  getSuggestions: (limit = 10) => axiosInstance.get('/discovery/suggestions', { params: { limit } }),
};


// Dashboard APIs
export const dashboardAPI = {
  getAthleteDashboard: () => axiosInstance.get('/dashboard/athlete'),
  getCoachDashboard: () => axiosInstance.get('/dashboard/coach'),
  getClubDashboard: () => axiosInstance.get('/dashboard/club'),
  getPhysioDashboard: () => axiosInstance.get('/dashboard/physio'),
  getNutritionistDashboard: () => axiosInstance.get('/dashboard/nutritionist'),
  getAITips: () => axiosInstance.post('/dashboard/ai-tips'),
  getEngagementAnalytics: (period = '7d') => axiosInstance.get('/dashboard/engagement-analytics', { params: { period } }),
  aiScoutSearch: (query) => {
    const formData = new FormData();
    formData.append('query', query);
    return axiosInstance.post('/dashboard/ai-scout', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  updateCareerGoals: (goals) => axiosInstance.put('/dashboard/career-goals', goals),
  updateStats: (stats) => axiosInstance.put('/dashboard/update-stats', stats),
  getApplications: () => axiosInstance.get('/dashboard/applications'),
  getReceivedApplications: () => axiosInstance.get('/dashboard/received-applications'),
  getTrialApplicants: (params) => axiosInstance.get('/dashboard/trial-applicants', { params }),
  updateTrialApplicationStatus: (applicationId, status) => axiosInstance.put(`/dashboard/trial-applications/${applicationId}/status?status=${status}`),
};


// Search APIs
export const searchAPI = {
  search: (query) => axiosInstance.get('/search', { params: { q: query } }),
};

// Notifications APIs
export const notificationsAPI = {
  getNotifications: (limit = 50) => axiosInstance.get('/notifications', { params: { limit } }),
  markAsRead: (notificationId) => axiosInstance.put(`/notifications/${notificationId}/read`),
  markAllAsRead: () => axiosInstance.put('/notifications/read-all'),
  getUnreadCount: () => axiosInstance.get('/notifications/unread-count'),
};

// Enquiries APIs
export const enquiriesAPI = {
  createEnquiry: (data) => {
    const formData = new FormData();
    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== undefined) {
        formData.append(key, data[key]);
      }
    });
    return axiosInstance.post('/enquiries', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getReceivedEnquiries: (status) => axiosInstance.get('/enquiries/received', { params: status ? { status } : {} }),
  getSentEnquiries: () => axiosInstance.get('/enquiries/sent'),
  updateStatus: (enquiryId, status) => {
    return axiosInstance.put(`/enquiries/${enquiryId}/status?status=${status}`);
  },
  markAsRead: (enquiryId) => axiosInstance.put(`/enquiries/${enquiryId}/read`),
};

export default axiosInstance;
